import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import styles from './date-range-filter.module.scss';
import { MultiOptionsFilterComponent } from 'features/insights/components/compare-bar/report-actions-bar/advance-filters-modal/multi-entities-filter/types';
import { ScrollTrap } from 'extensions/scroll-trap/scroll-trap';
import { useSelector } from 'react-redux';
import { DatePicker } from 'ui-components/date-picker/date-picker';
import { RangeData } from 'ui-components/date-picker/date-types/RangeData';
import { DATE_FORMAT } from 'features/chains/common/constants';
import moment, { Moment } from 'moment';
import { APP_CONSTS } from 'core/constants/app-consts';
import { FilterOperator } from '@placer-ui/types';
import {
    useDateRangeToDatePresetSection,
    useDateRangeToPreset,
} from 'features/insights/components/filters/date-filter/use-segment-to-date';
import { convertChosenLabel } from 'features/insights/components/filters/date-filter/date-filters-utils';
import { labelSuggestion } from 'utils/date/date';
import { selectReportDefaultDates } from 'features/insights/store/selectors/insights-selectors';
import { getFeatureFlags } from 'core/flow-control';
import {
    Button,
    CalendarIcon,
    DatePicker as DsDatePicker,
    useTheme,
    UtilityChevronIcon,
} from '@placer-ui/components';

export const DateFilter: MultiOptionsFilterComponent<string[]> = ({ filter, onChange }) => {
    const { accessibility_advance_filters_modal_ff } = getFeatureFlags();
    const datePickerRef = React.createRef<HTMLDivElement>();
    const datePickerBtnRef = React.createRef<HTMLButtonElement>();
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const dateLimits = useSelector(selectReportDefaultDates);
    const [date_start, date_end, date_label, date_preset] = filter?.value ?? [];
    const findDate = useDateRangeToPreset();
    const getActiveSection = useDateRangeToDatePresetSection();
    const { zIndexes } = useTheme();
    const dateLabel = date_preset
        ? date_preset.includes('|')
            ? date_preset
            : '|' + date_preset
        : date_label;

    const onDateFilterChange = useCallback(
        (newDateValue: RangeData<Moment>) => {
            const newDateStart = moment(newDateValue.value[0]).format(DATE_FORMAT);
            const newDateEnd = moment(newDateValue.value[1]).format(DATE_FORMAT);
            if (newDateStart === date_start && newDateEnd === date_end) return;
            const value = [newDateStart, newDateEnd];
            newDateValue.label && value.push(newDateValue.label);
            onChange({
                operator: APP_CONSTS.filter.operators.BETWEEN as FilterOperator,
                value,
            });
        },
        [date_end, date_start, onChange],
    );
    const activePanel = getActiveSection({
        date_start,
        date_end,
        date_label,
    });

    const selectedLabel =
        convertChosenLabel(dateLabel) ||
        findDate({
            date_start,
            date_end,
            date_label: dateLabel,
        })?.chosenLabel ||
        labelSuggestion(date_start, date_end);

    if (accessibility_advance_filters_modal_ff) {
        return (
            <div className={styles.datePickerContainer}>
                <DsDatePicker
                    zIndex={zIndexes.modal + 1}
                    maxMonth={moment(dateLimits.maxDate).subtract(1, 'month').toDate()}
                    minDate={dateLimits.minDate}
                    startDate={date_start}
                    endDate={date_end}
                    onChange={({ value, label }) => {
                        onDateFilterChange({
                            value: [moment(value[0]), moment(value[1])],
                            label,
                        });
                        datePickerBtnRef.current?.focus();
                    }}
                    extraInitialData={{ label: selectedLabel, activePanel }}
                    trigger={
                        <Button
                            ref={datePickerBtnRef}
                            fullWidth
                            testId="date-filters-button-advanced"
                            variant="secondary-gray"
                            LeftIcon={<CalendarIcon />}
                            RightIcon={<UtilityChevronIcon />}
                            alignment="space-between"
                        >
                            {selectedLabel}
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div
            ref={datePickerRef}
            className={styles.container}
            data-test-id="property-date-picker-filter"
        >
            <ScrollTrap stopScroll={isDatePickerOpen}>
                <DatePicker
                    {...dateLimits}
                    triggerClassName={cn('dialog-report-filter-datepicker', styles.filterTrigger)}
                    pickerClassName={styles.filter}
                    getPopupContainer={() => datePickerRef.current!}
                    startDate={date_start}
                    endDate={date_end}
                    displayText={selectedLabel}
                    visible={isDatePickerOpen}
                    onVisibleChange={setIsDatePickerOpen}
                    onChange={onDateFilterChange}
                    hideNavigation={false}
                    overlayClassName={styles.overlay}
                    showMaxDate={false}
                />
            </ScrollTrap>
        </div>
    );
};
